/**
 * Scrips Scheduling API — TypeScript Contracts
 * Generated: 2026-04-04
 * Source: Scripsteam/Scrips.AppointmentScheduling (.NET backend)
 *
 * These types match the .NET backend exactly. The React app must use
 * these types for all API communication with the scheduling service.
 *
 * Auth: JWT Bearer token (all endpoints require [Authorize])
 * Timezone: All dates converted via user's `zoneinfo` JWT claim
 * Tenant: `organizationId` header required by middleware
 */

// === Shared / Core Types ===

// `definition` ships from the wire but consumers everywhere treat it as
// optional with `?? ''` fallbacks (see appointment-profile-section,
// provider-selection-view, transform-form-to-request). Marking it
// optional matches that contract and unblocks fixtures that don't
// bother including it.
/**
 * `Coding` mirrors the Flutter `AppointmentValueSets` model
 * (`scrips_shared_features/lib/features/common/data/datamodels/appointment_value_sets_model.dart`)
 * which carries 4 nullable string fields: `code` / `definition` /
 * `display` / `system`. Three React aliases narrow this same shape
 * (`AppointmentStatus`, `ReasonCode`, `AppointmentCancellationReason`).
 * All four include `system?: string | null` — Flutter ships JSON
 * `null` when the value-set entry has no system (book-side
 * constructors do the same: `transform-form-to-request.ts`).
 */
export interface Coding {
  code: string;
  display: string;
  definition?: string | null;
  system?: string | null;
}

export interface AppointmentStatus {
  code: string;
  display: string;
  definition?: string | null;
  system?: string | null;
}

export interface AppointmentType {
  code: string;
  display: string;
  // The wire ships `definition: null` and `system: null` for the
  // appointment-type ship-shape; consumers everywhere read with `??`.
  definition?: string | null;
  system?: string | null;
}

export interface ReasonCode {
  code: string;
  display: string;
  definition?: string | null;
  system?: string | null;
}

export interface ServiceCategory {
  code: number;
  display: string;
}

export interface ServiceType {
  code: number;
  display: string;
}

/**
 * Flutter wire shape for a speciality on the BookAppointment payload —
 * `{ specialitySkillId, code: { code, displayName, system, description, shortName } }`
 * — NOT the simpler `{ code, display }` shape we used internally for
 * filtering. Backend's gRPC `GetSpecialitiesByCodesAsync` looks the
 * appointment up by `code.code` (a SNOMED id like "394814009").
 */
export interface SpecialityCoding {
  code: string;
  displayName?: string;
  system?: string | null;
  description?: string | null;
  shortName?: string | null;
}

export interface SpecialityWire {
  specialitySkillId?: string;
  code?: SpecialityCoding;
}

/** Internal-only shape used by calendar/list filters; not the wire shape. */
export interface Speciality {
  code: Coding;
  display: string;
}

export interface LocationModel {
  id: string;
  name: string;
  imageUrl?: string;
  isArchived?: boolean;
  description?: string;
}

export interface AppointmentCancellationReason {
  code: string;
  display: string;
  definition?: string | null;
  system?: string | null;
}

export interface ParticipantResponse {
  referenceType: string;
  reference: string;
  patient?: PatientResponse;
  provider?: ProviderResponse;
}

export interface PatientResponse {
  id: string;
  firstName: string;
  /** Optional middle name — present on wider responses (e.g. FU
   *  request `patient` shape from `/api/Appointment/Request`).
   *  Backend ships `""` when absent. Added 2026-05-20 for PROD-849
   *  (FU PR-2 review §5 — removes the only `as unknown as` cast in
   *  the FU tab production code). */
  middleName?: string | null;
  lastName: string;
  /** Pre-composed display name (`"Khadija Devtest"`). Some endpoints
   *  ship this alongside firstName/lastName as a convenience. Added
   *  2026-05-20 for PROD-849 (same review item as `middleName`). */
  name?: string | null;
  /** Avatar URL — falls back to initials when missing. Backend may use either `photo` or `photoUrl`. */
  photo?: string;
  /** Backend alias for `photo` — matches Flutter's PatientData.photoUrl. */
  photoUrl?: string;
  /** ISO date or pre-formatted string; the day-list cell renders + computes age. */
  dateOfBirth?: string;
  /** Backend alias for `dateOfBirth` — matches Flutter's PatientData.birthDate. */
  birthDate?: string;
  /**
   * Pre-formatted age string from the backend (e.g. "37y", "1y 4m 4d").
   * Flutter renders this directly; we prefer it over local computation
   * to keep the threshold logic (under 2y → Y/M/D, otherwise just Y) on
   * the server.
   */
  age?: string;
  /**
   * Patient gender — lowercase string per the wire ("female", "male",
   * "other"). Present on the FU request `patient` shape (verified
   * fixture + dev curl 2026-05-22) and on the wider PreBook responses.
   * Added 2026-05-23 for PROD-920 (the FU "Create Follow Up" handler
   * seeds `form.patientGender` from this field).
   */
  gender?: string;
  /**
   * Short ISO date — wider Patient shapes use `dateOfBirth` /
   * `birthDate` instead, but some endpoints (notably the FU request
   * `patient`) historically shipped this field. Kept optional so call
   * sites can safely `?.dob` without a cast.
   */
  dob?: string;
  /**
   * Short uppercase tag rendered next to the patient name (e.g. "BM" for
   * a special-needs / clinical-priority indicator). Backend field name
   * is still being confirmed; defensive optional.
   */
  specialBadge?: string;
  /**
   * Patient importance / priority signal. Flutter ships `importance` as
   * `{ code, display, definition }`; `code === 'VIP'` adds a green ring
   * to the avatar (see PatientAvatar atom).
   */
  importance?: {
    code?: string;
    display?: string;
    definition?: string;
  };
  // Wire fields the View Appointment popup reads (PatientResponse on
  // the wire is broader than the slim shape we originally typed). Same
  // identifiers as the rest of the patient surface.
  identificationId?: string;
  mrn?: string;
  phoneNumber?: string;
}

// Provider responses come from the wire fully-populated, but compact
// fixtures and child-component prop shapes typically only carry id +
// name (the rest is computed downstream). Relax the optional fields
// so fixtures and partial-row literals type-check.
export interface ProviderResponse {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  photo?: string;
  speciality?: Speciality;
  timeZone?: string;
}

export interface PracticeResponse {
  id: string;
  name: string;
}

export interface RoomResponse {
  id: string;
  name: string;
}

export interface SlotResponse {
  id: string;
  start: string;
  end: string;
  status?: string;
  comment?: string;
}

/**
 * Flag entry — Flutter wire shape on BookAppointment is
 * `{ code: number, display: string, categoryName: string | null }`.
 * Internal/list endpoints use `code: string` and `category` so we keep
 * those optional and let the booking transform pick the right shape.
 */
export interface FlagResponse {
  id?: string;
  code: string | number;
  display: string;
  category?: string;
  categoryName?: string | null;
}

export interface Sponsor {
  id: string;
  name: string;
  /** Flutter `PayerDetails.payerName` — display name shown on the invoice. */
  payerName?: string;
  /** Logo URL for the payer (Flutter PayerDetails.imageUrl). */
  imageUrl?: string;
  /**
   * Payer's DHPO / billing company code. The backend serializes the
   * top-level request `sponsor` into the invoice's stored `SponsorDetails`;
   * if this is missing, the downstream eRx→DHPO transaction ships an empty
   * `ReceiverID`/`PayerID` and is rejected on the MinLength schema rule.
   * MUST be sent on insurance/corporate saves (Flutter parity); null on self-pay.
   */
  payerCompanyId?: string | null;
  /**
   * Related TPAs (Flutter `PayerDetails.relatedTpa`). `relatedTpa[0]` drives
   * the DHPO receiver on TPA-mediated payers. Sent whole so the stored
   * sponsor matches Flutter.
   */
  relatedTpa?: unknown[] | null;
}

export interface BillingTotal {
  grossTotal: number;
  discountTotal: number;
  netTotal: number;
  patientShareTotal: number;
  insuranceAmountTotal: number;
}

export interface AppointmentInvoiceModel {
  id: string | null;
  invoiceDate?: string;
  draftedOn?: string;
  /**
   * Invoice status — one of "Draft" / "Issued" / "Balanced". Used by the
   * client-side Invoice-Statuses filter (Flutter `applyInvoiceFilter`).
   */
  invoiceStatus?: string;
  /** Pricing totals — populated on detail responses (PreLoadAppointmentDetails
   *  + Appointment/GetById). Flutter parity for self_pay / insurance /
   *  corporate child widgets. */
  grossTotal?: number;
  discountTotal?: number;
  netTotal?: number;
  patientPayable?: number;
  insuranceAmount?: number;
  patientShare?: number;
  currency?: string;
}

/**
 * Appointment status codes — what the backend stores on
 * `appointment.status.code` and what the calendar's filter pills/dropdown
 * write into the appointments-request `status[]` body.
 *
 * Source: Flutter `app_constants.dart` — appointmentStatus* constants.
 */
export const APPOINTMENT_STATUS_CODES = [
  'Booked',
  'Arrived',
  'CheckedIn',
  'Fulfilled',
  'Cancelled',
  'NoShow',
  'Proposed',
  'Pending',
  'Waitlist',
  'EnteredInError',
] as const;
export type AppointmentStatusCode = (typeof APPOINTMENT_STATUS_CODES)[number];

/** Invoice status options for the client-side Invoice-Statuses filter. */
export const INVOICE_STATUS_OPTIONS = [
  'Draft',
  'Issued',
  'Balanced',
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUS_OPTIONS)[number];

export interface TimeModel {
  hour: number;
  minute: number;
  format: string;
}

export interface RecurringInfo {
  id?: string;
  startDate: string;
  endDate: string;
  repeatEvery: number;
  numberOfRepeat: number;
  period: string;
  days?: string[];
}

// === Appointment Profile ===

/**
 * Previous appointment a FOLLOWUP is linked to. Flutter
 * `CompletedAppointmentResponses` (appointment_model.dart:1261).
 */
export interface CompletedAppointmentResponse {
  appointmentId?: string | null;
  appointmentProfile?: CreateAppointmentProfileRequest | null;
  appointmentDate?: string | null;
  reasonCode?: ReasonCode | null;
}

export interface CreateAppointmentProfileRequest {
  id?: string;
  profileName: string;
  organizationId: string;
  practiceId: string;
  practionerId: string;
  billingProfileId?: string;
  color: string;
  appointmentType: AppointmentType;
  reasonCode?: ReasonCode;
  /** Slot length in minutes — drives the quick-book chips on the provider card. */
  minutesDuration?: number;
  /**
   * Location category — key for the chip's icon.
   * Backend values: "office" | "home" | "virtual" | "block".
   */
  location?: LocationModel;
  speciality?: Speciality;
  specialityId?: string;
  /**
   * Configured room for the profile. The booking wire sends this as the
   * appointment's `roomId`; the backend resolves it via `GetRoomByIdAsync`.
   * A missing / empty / all-zero room makes the appointment un-openable +
   * un-reschedulable (the resolver 500s/empties — reproduces in Flutter too),
   * so selection prefers profiles with a usable room (see
   * `quick-create-slot.ts#hasUsableRoom`).
   */
  roomId?: string;
  /** Profile-template id (distinct from the instance `id`); used to re-resolve. */
  referenceId?: string;
  inAppBooking?: boolean;
  isDefault?: boolean;
  isUpdated?: boolean;
  /**
   * True when the provider has marked this profile as their preferred /
   * default for that location. The quick-book chip displays this profile's
   * duration + color when present (Flutter calendar_tab.dart:4324).
   */
  isPreferred?: boolean;
  /** Archived profiles are never used for Calendar slots (`practice-slot-profile.ts`). */
  isArchived?: boolean;
}

export interface CreateReminderProfileRequest {
  id?: string;
}

export interface QuestionnaireForm {
  id?: string;
  questionnaireId?: string;
  name?: string;
  status?: string;
}

export interface PatientAppointmentPayerDetailModel {
  id?: string | null;
}

// === Main Response Models ===

export interface AppointmentResponse {
  id: string;
  start?: string;
  end?: string;
  status: AppointmentStatus;
  /** Why the appointment was cancelled (PreLoadAppointmentDetails; ONE `l`
   *  in `cancelation`, backend spelling). Absent / null unless cancelled. */
  cancelationReason?: AppointmentCancellationReason | null;
  /** Free-text cancellation note (TWO `l`s, backend spelling) — typed by staff
   *  in the Cancel dialog or by a patient in WhatsApp. */
  cancellationNote?: string | null;
  /** Human booking reference (Flutter `appointment.referenceId`) — shown in
   *  the Edit-Appointment "APPOINTMENT STATUS" summary. */
  referenceId?: string;
  location: LocationModel;
  organizationId: string;
  practiceId: string;
  roomId: string;
  /** Backend ships the resolved room object on detail responses (e.g.
   *  PreLoadAppointmentDetails). List endpoints only return `roomId`. */
  room?: RoomResponse;
  appointmentType: AppointmentType;
  reasonCode: ReasonCode;
  specialty: Speciality;
  priority: number;
  description?: string;
  comment?: string;
  patientInstruction?: string;
  minutesDuration: number;
  slot?: SlotResponse;
  participant: ParticipantResponse[];
  appointmentProfile: CreateAppointmentProfileRequest;
  reminderProfile?: CreateReminderProfileRequest;
  questionnaireForm?: QuestionnaireForm;
  invoice: AppointmentInvoiceModel;
  flagList?: FlagResponse[];
  sponsorType?: string;
  sponsor?: Sponsor;
  policyNo?: string;
  billingTotal: BillingTotal;
  paymentLink?: string;
  recurring?: RecurringInfo;
  patientAppointmentPayerDetail?: PatientAppointmentPayerDetailModel;
  updatedBy: string;
  isBlock?: boolean;
  residentialAddressId?: string;
  callBackContractId?: string;
  addedServices?: string[];
  /**
   * FOLLOWUP link — the appointment this one follows up on.
   *
   * Load-bearing for the edit/reschedule save gate: the form schema blocks
   * Save on a FOLLOWUP with no reference (appointment-form.ts superRefine,
   * mirroring Flutter `saveAppointmentButtonStatus`,
   * scheduling_bloc.dart:1341-1350). Both fields were MISSING from this
   * interface, so `appointment-to-form-values.ts` could not see them, could
   * not seed the form, and every follow-up opened into an un-savable
   * edit/reschedule panel.
   *
   * Flutter's edit branch accepts EITHER the flat id OR
   * `completedAppointmentResponses.appointmentId` — the backend ships the
   * link in the nested object for appointments booked through the follow-up
   * flow (Flutter `appointment_model.dart:1261-1298`).
   */
  referenceAppointmentId?: string | null;
  completedAppointmentResponses?: CompletedAppointmentResponse | null;
}

export interface ListAppointmentResponse {
  id: string;
  start: string;
  end: string;
  status: AppointmentStatus;
  location: LocationModel;
  room?: RoomResponse;
  appointmentType: AppointmentType;
  reasonCode: ReasonCode;
  description?: string;
  slot?: SlotResponse;
  comment?: string;
  participant: ParticipantResponse[];
  appointmentProfile: CreateAppointmentProfileRequest;
  flagList?: FlagResponse[];
  // Fields the `ByPatientId` flat rows ship that the calendar grid omits
  // (verified against the live response 2026-06-02). Optional because the
  // calendar `appointments[]` rows don't carry them.
  /** Bare speciality GUID — used by the patient tab's speciality-chip client filter. */
  specialityId?: string;
  /** Speciality envelope (`code.displayName` is the label). NOT the `{code,display}` shape. */
  specialty?: SpecialityWire;
  /** Non-null ⇒ part of a recurring series (drives the "View Recurring Series" row action). */
  recurringId?: string | null;
  /** Block appointment (no patient/provider) — hides the recurring indicator. */
  isBlock?: boolean;
}

export interface ListAppointmentResponseByMonth extends ListAppointmentResponse {
  appointmentDate: string;
  provider: ProviderResponse;
  invoice: AppointmentInvoiceModel;
  sponsor?: Sponsor;
  policyNo?: string;
  sponsorType?: string;
  recurringId?: string;
  specialty: Speciality;
  specialityId: string;
}

/**
 * Patient-chart Appointments tab — wire shape of `POST /api/Appointment/ForPatient`
 * (Flutter `appts_tab.dart` → `patientAppointments` datasource). The rows are
 * the SAME `Appointment` objects the calendar list returns (provider / patient
 * nested in `participant[]`, `specialty.code.displayName` is the label), plus
 * three fields the chart cards read that the calendar grid omits — `practice`,
 * `created`, `createdPerson`. Curl-verified 2026-06-04.
 *
 * Distinct from `/api/Appointment/ByPatientId` (the patient-EDIT tab's facet
 * endpoint → `AppointmentsDetailsResponse`): different path, simpler request
 * (`includePrevious` instead of filters), different response wrapper.
 */
export interface AppointmentForPatient extends ListAppointmentResponse {
  practice?: {
    id?: string;
    name?: string;
    phone?: string;
    address?: string;
  } | null;
  /** Audit create timestamp (ISO, no `Z`). */
  created?: string | null;
  /** Display name of the user who created the appointment. */
  createdPerson?: string | null;
}

export interface AppointmentForPatientResponse {
  currentPage?: number | null;
  totalPage?: number | null;
  count?: number | null;
  appointments?: AppointmentForPatient[] | null;
}

// === Calendar Slots (POST /api/v1/Practice/calendarSlots) ===
// Returns provider-organized slot grid used by Day/Week/Month/By-Doctor views.
// Mirror of Flutter's CalendarSlotsRequest / DoctorListResponse.

export interface AppointmentValueSets {
  code?: string;
  display?: string;
  definition?: string;
}

export interface WorkingHour {
  weedayId?: number;
  startWeekDay?: string;
  endWeekDay?: string;
  /** ISO time-of-day from backend, e.g. "09:00:00". */
  startTime?: string;
  /** ISO time-of-day from backend, e.g. "18:00:00". */
  endTime?: string;
  dayOff?: unknown;
}

/**
 * A single time block on the calendar grid. Note: this carries NO patient
 * identity — Flutter's calendar tile shows status + comment, and clicks fetch
 * full appointment details. To display a patient name in the tile, fetch the
 * appointment via /api/Appointment/{id}.
 */
export interface AppointmentSlot {
  appointmentType?: AppointmentValueSets;
  start?: string;
  end?: string;
  comment?: string;
  overbooked?: boolean;
  practiceId?: string;
  providerId?: string;
  practiceName?: string;
}

/**
 * Slot shape sent inside `CreateAppointmentRequest.recurring.slots`.
 * Mirrors Flutter `recurrence_properties_model.dart:108-110` —
 * specifically the `null`-tolerant variant the dev curl ships (2026-05-26):
 * `appointmentType`, `comment`, `practiceName` arrive as explicit `null`,
 * not omitted. The backend's
 * `AppointmentController.ConvertCreateAppointmentCommandTimeToUtc`
 * iterates these and dereferences inner fields during UTC conversion;
 * omitting any of them triggers an NPE (PROD-961).
 *
 * Kept distinct from `AppointmentSlot` (the response shape) so callers
 * of the response type don't need to handle nulls everywhere.
 */
export interface RecurringSlot {
  appointmentType?: AppointmentValueSets | null;
  start?: string;
  end?: string;
  comment?: string | null;
  overbooked?: boolean;
  practiceId?: string;
  providerId?: string;
  practiceName?: string | null;
}

/**
 * Provider with their slots — one entry per practitioner the calendarSlots
 * endpoint returns. Flutter calls this DoctorListResponse but it's reused
 * for the slot grid. We name it ProviderWithSlots for clarity.
 */
export interface ProviderWithSlots {
  id?: string;
  name?: string;
  photo?: string;
  speciality?: Speciality;
  /** Multi-specialty list — Flutter PM derives the chip set from this. */
  doctorSpecialities?: Speciality[];
  doctorSpecialityIds?: string[];
  timeZone?: string;
  workingHours?: WorkingHour[];
  slots?: AppointmentSlot[];
  /** Per-provider configured appointment profiles (each carries minutesDuration). */
  appointmentProfile?: CreateAppointmentProfileRequest[];
  /**
   * The profile the backend counted `totalSlots` with, at the requested
   * practice (additive, nullable; PM calendarSlots fix, 19 Oct). Same rule as
   * `chooseSlotProfile` in `scheduling/lib/practice-slot-profile.ts`.
   */
  slotProfileId?: string | null;
  /**
   * The gateway (Scrips.Practice.Aggregator #169) says this practitioner has no non-archived appointment profile at
   * the requested practice. Additive: absent on older gateways, where the local rule decides alone.
   */
  noProfileAtPractice?: boolean;
  totalSlots?: number;
  organizationId?: string;
  specialityId?: string;
  serviceCategoryId?: number;
  serviceTypeId?: number;
}

export type CalendarSlotsResponse = ProviderWithSlots[];

/**
 * A single practice exam room. Mirrors Flutter `ExamRoom`
 * (`settings_module/data/datamodels/exam_room_list_model.dart`).
 * `examRoom` is the display name (the By-Room column label); `examHourId`
 * is the room's id.
 */
export interface ExamRoom {
  examHourId?: string | null;
  examRoom?: string | null;
}

/**
 * `GET /api/Practice/EditPracticeExamRoom/{practiceId}` response element.
 * The endpoint returns a LIST; Flutter reads `[0].examRooms` for the
 * practice's room set (`calendar_screen.dart:2240`). Mirrors Flutter
 * `ExamRoomList`. Note the base is `/api/Practice` (NOT `/api/v1/Practice`
 * — calendarSlots uses v1, this one does not).
 */
export interface ExamRoomList {
  practiceId?: string | null;
  noOfExamHours?: number | null;
  examRooms?: ExamRoom[] | null;
  deletedId?: string[] | null;
}

/**
 * Practice-wide working hours profile.
 * Endpoint: GET /api/Practice/WorkingHoursList/{practiceId}
 *
 * `type === 1` is the standard work-hours profile; other values cover
 * break / closed / holiday windows. The day-view gutter uses type 1
 * filtered to the displayed weekday's `weedayId`.
 */
export interface PracticeWorkingHourList {
  practiceId?: string;
  workingHoursId?: string;
  type?: number;
  profileName?: string;
  /** dd-MM-yyyy */
  startDate?: string;
  /** dd-MM-yyyy */
  endDate?: string;
  practiceWorkingSlots?: WorkingHour[];
}

export interface CalendarSlotsRequest {
  organizationId: string;
  practiceId: string;
  startDate: string;
  endDate: string;
  practitioners: string[];
  searchText?: string;
  pageNumber?: number;
  pageSize?: number;
}

// === Device calendar cards (PROD-2181 / PROD-2184) ===

/**
 * `POST /api/v1/Practice/DeviceCalendarSlots` — the Devices tab's card feed.
 *
 * Deliberately NOT `CalendarSlotsRequest` (ruling 2026-09-13, Andrew: "create
 * the same idea not use the same api"). The provider request carries no actor
 * discriminator, so teaching calendarSlots to answer devices would mix machines
 * into every existing provider consumer — and would charge each device row for
 * a photo SAS URI, a licence-expiry check and four practitioner-only objects a
 * machine has no use for.
 */
export interface DeviceCalendarSlotsRequest {
  organizationId: string;
  practiceId: string;
  startDate: string;
  endDate: string;
  /** Case-insensitive match on device name or subtype. Empty: every device. */
  searchText?: string;
  /** Restrict to these device ids. Empty: every live device at the practice. */
  devices?: string[];
  /** How many free slots to carry on each card. The strip shows 3. */
  slotCount?: number;
  /**
   * PROD-2456. Cut the device's slots at this product's minutes. Omitted: the
   * device's default product. A foreign or archived product is refused (400).
   */
  appointmentProfileId?: string;
}

/** One product of the device — the quick-book chip. */
export interface DeviceSlotsAppointmentProfile {
  id: string;
  profileName: string;
  minutesDuration: number;
  color: string | null;
  appointmentTypeId: string | null;
  isDefault: boolean;
  inAppBooking: boolean;
  billingProfileId: string | null;
  serviceId: string | null;
}

/**
 * One device card.
 *
 * No photo, speciality, licence or issue: a machine has none of them, and an
 * empty practitioner field on a device row reads to `ProviderCard` as
 * "credentials missing" and draws a warning badge on a machine.
 */
export interface DeviceWithSlots {
  id: string;
  name: string;
  /** The card's second line. Null when the device has no technician. */
  technicianName: string | null;
  technicianId: string | null;
  subtype: string | null;
  practiceId: string;
  timeZone: string | null;
  /**
   * `weedayId` is Mon=1..Sun=7 — the same convention the practitioner path
   * emits, which is what lets `workingHoursLabel` read a device row unchanged
   * (it falls back to `start`/`end` when `startTime`/`endTime` are absent).
   * Empty when the device has no active Work profile: known, but not bookable.
   */
  workingHours: { weedayId: number; start: string; end: string }[];
  appointmentProfile: DeviceSlotsAppointmentProfile[];
  /** The first `slotCount` free slots. `totalSlots` counts them all. */
  slots: { start: string; end: string }[];
  totalSlots: number;
}

export type DeviceCalendarSlotsResponse = DeviceWithSlots[];

// === Request Models ===

export type DateFilter = 'Day' | 'Week' | 'Month' | 'Room' | 'Doctor';

export interface AppointmentsRequest {
  startDate: string;
  endDate: string;
  practitioners?: string[];
  practices?: string[];
  status?: Coding[];
  location?: string[];
  types?: Coding[];
  filter: DateFilter;
  pageNumber: number;
  pageSize: number;
  searchText?: string;
  organizationId: string;
  practiceId?: string;
  /** IANA timezone, e.g. "Asia/Dubai". Flutter sends this as `LoggedInUserTimezone`. */
  LoggedInUserTimezone?: string;
  isTimeSelected?: boolean;
  showActiveAppointmentOnly?: boolean;
}

/**
 * `POST /api/Appointment/ResourceList` — the DEVICE appointment list (PROD-2382).
 *
 * Identical to `AppointmentsRequest` except that the participant filter is
 * `devices`, not `practitioners`. Deliberately its own request type and its own
 * route, for the same reason `DeviceCalendarSlotsRequest` is (ruling
 * 2026-09-13, Andrew: "create the same idea not use the same api").
 *
 * The provider route has no actor discriminator: its participant filter matches
 * on reference alone with no type check, which is the only reason stuffing
 * device ids into `practitioners` ever worked. It is an accident, not a
 * contract — a provider-only narrowing on that filter would have silently
 * emptied the Devices tab and the Technician Worklist.
 *
 * The RESPONSE is unchanged — the same `AppointmentsDetailsResponse` the
 * provider route returns, which is already device-safe (`provider` is null for
 * a device booking, `specialty` null since PROD-2287, and a `Device`
 * participant maps like any other).
 */
export interface DeviceAppointmentsRequest {
  startDate: string;
  endDate: string;
  /** Device ids. The device-side twin of `AppointmentsRequest.practitioners`. */
  devices?: string[];
  practices?: string[];
  status?: Coding[];
  location?: string[];
  types?: Coding[];
  filter: DateFilter;
  pageNumber: number;
  pageSize: number;
  searchText?: string;
  organizationId: string;
  practiceId?: string;
  /** IANA timezone, e.g. "Asia/Dubai". Flutter sends this as `LoggedInUserTimezone`. */
  LoggedInUserTimezone?: string;
  isTimeSelected?: boolean;
  showActiveAppointmentOnly?: boolean;
}

/**
 * Reminder profile (Flutter `ReminderProfile`) — full object shipped on
 * the booking payload, including the per-rule `reminderProfileType`
 * array.
 */
export interface ReminderProfileTypeRule {
  id?: string;
  type?: string;
  time?: string;
  whenToSend?: string;
  number?: string;
}

export interface ReminderProfileFull {
  id?: string;
  organizationId?: string;
  practiceId?: string;
  practitionerId?: string;
  profileName?: string;
  customNote?: string;
  enableInAppBooking?: boolean;
  isDefault?: boolean;
  isUpdated?: boolean;
  reminderProfileType?: ReminderProfileTypeRule[];
}

/**
 * Residential address (Flutter `PatientAddress`) — full object that
 * ships alongside `residentialAddressId` on the booking payload for Home
 * visits.
 */
export interface ResidentialAddress {
  id?: string;
  building?: string | null;
  apartment?: string | null;
  country?: string | null;
  city?: string | null;
  address?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  isDefault?: boolean;
  phoneNumber?: string | null;
  relation?: { id?: string; name?: string };
  locationName?: string | null;
  notes?: string | null;
  imageUrl?: string | null;
  name?: string | null;
  isResidential?: boolean;
}

/**
 * Invoice — Flutter `Invoice` model. Backend reads this as the live
 * billing snapshot for the appointment; sending `billingTotal` (a
 * different field) under-populates the read path on the next fetch.
 */
export interface InvoiceRequest {
  organization?: unknown | null;
  id?: string | null;
  providerName?: string | null;
  resubmissionAttachmentFileUrl?: string | null;
  resubmissionAttachmentFileName?: string | null;
  patientId?: string | null;
  providerId?: string | null;
  practiceId?: string | null;
  resubmissionComment?: string | null;
  invoiceNumber?: string | null;
  invoiceStatus?: string | null;
  policyNo?: string | null;
  sponsorType?: string | null;
  sponsor?: unknown | null;
  provider?: unknown | null;
  appointmentId?: string | null;
  trnNumber?: string | null;
  encounterId?: string | null;
  locationId?: string | null;
  encounterNumber?: string | null;
  managementDiscount?: number | null;
  invoiceDate?: string | null;
  draftedOn?: string | null;
  encounterDate?: string | null;
  patient?: unknown | null;
  practice?: unknown | null;
  claim?: unknown | null;
  diagnosisList?: unknown | null;
  services?: unknown | null;
  note?: string | null;
  grossTotal?: number;
  discountTotal?: number;
  netTotal?: number;
  patientPayable?: number;
  deposited?: number;
  sponsorOutstanding?: number;
  sponsorPaymentTotal?: number;
  patientPaymentStatus?: number;
  patientOutstanding?: number;
  excludedTotal?: number;
  patientShareTotal?: number;
  sponsorAmountTotal?: number;
  eligibilityEndDate?: string | null;
  eligibilityStartDate?: string | null;
  errors?: unknown | null;
  assessmentPlan?: unknown | null;
}

export interface CreateAppointmentRequest {
  // Service category / type fields — Flutter wire ships explicit
  // `null` (not omitted) when there's no billing-config source for
  // them. The transform now ships null rather than the prior
  // hardcoded `{code:1, display:"Adoption"}` placeholder which was
  // landing wrong category metadata on every save (verified against
  // dev curl 2026-05-22).
  serviceCategoryId?: number | null;
  serviceTypeId?: number | null;
  serviceCategory?: ServiceCategory | null;
  serviceType?: ServiceType | null;
  /** SNOMED-shaped speciality (Flutter wire shape). */
  specialty: SpecialityWire;
  specialityId?: string;
  appointmentType: AppointmentType;
  location: LocationModel;
  organizationId: string;
  practiceId: string;
  roomId?: string;
  isBlock?: boolean | null;
  recurringId?: string | null;
  reasonCode: ReasonCode;
  priority: number;
  description?: string | null;
  start?: string;
  end?: string;
  minutesDuration?: number;
  comment?: string | null;
  patientInstruction?: string | null;
  patientId: string;
  practitionerId: string;
  paymentLink?: string | null;
  appointmentProfile: CreateAppointmentProfileRequest;
  reminderProfile?: ReminderProfileFull;
  isCreatedByPatient: boolean;
  questionnaireForm?: QuestionnaireForm | null;
  questionnaireFormId?: string | null;
  questionnaire?: unknown | null;
  flagList?: FlagResponse[];
  referenceAppointmentId?: string | null;
  followUpId?: string | null;
  isRelatedPerson: boolean;
  createdBy: string;
  updatedBy?: string | null;
  sponsorType?: string;
  sponsorId?: string;
  patientAppointmentPayerDetail?: PatientAppointmentPayerDetailModel | null;
  /** Full Flutter Invoice — replaces `billingTotal` on the wire. */
  invoice?: InvoiceRequest;
  sponsor?: Sponsor | null;
  policyNo?: string | null;
  residentialAddressId?: string;
  callBackContractId?: string;
  residentialAddress?: ResidentialAddress;
  createdPerson?: unknown | null;
  updatedPerson?: unknown | null;
  appointmentStatus?: string | null;
  addedServices?: unknown[];
  addedIndividualServices?: unknown[];
  recurring?: RecurringRequest | null;
  editRecurring?: unknown | null;
}

export interface RecurringRequest {
  /**
   * Pre-existing recurrence id when editing; `null` on create. Flutter
   * always emits this key (`recurrence_properties_model.dart:99`); the
   * .NET deserializer treats absent as different from explicit null on
   * some `CreateAppointmentCommand` paths, so we MUST emit it
   * unconditionally (PROD-961 follow-up to the NPE chain at
   * AppointmentController.ConvertCreateAppointmentCommandTimeToUtc).
   */
  id?: string | null;
  startDate: string;
  endDate: string;
  repeatEvery: number;
  numberOfRepeat: number;
  period: 'Days' | 'Weeks' | 'Months' | 'Years';
  /**
   * Weekday names ("Monday" ...). Flutter only emits this list when
   * `period === 'Weeks'`; for Days/Months/Years it ships `null`
   * (`recurrence_properties_model.dart:105`). Sending an empty array
   * outside Weeks is what triggered the backend NPE.
   */
  days?: string[] | null;
  /**
   * Pre-calculated occurrence slots from POST /api/Appointment/RetrieveSlots.
   * Flutter ships the full `AppointmentSlot` shape (with `appointmentType`,
   * `overbooked`, `practiceId`, `providerId`, `practiceName`) per
   * `recurrence_properties_model.dart:108-110`. The backend's
   * `ConvertCreateAppointmentCommandTimeToUtc` iterates these to convert
   * each start/end to UTC — shipping `{ start, end }` only (no
   * `practiceId`, etc.) hit an NPE downstream when other fields were
   * dereferenced. Uses `RecurringSlot` (the null-tolerant wire variant)
   * rather than `AppointmentSlot` (the response variant) so callers of
   * the response type don't need to handle nulls everywhere.
   */
  slots?: RecurringSlot[];
}

export interface SlotDto {
  id: string;
  appointmentType: AppointmentType;
  scheduleId: string;
  status: string;
  start: string;
  end: string;
  comment?: string;
  overbooked: boolean;
  providerId: string;
  practiceId: string;
  practiceName?: string;
}

export interface CancelAppointmentCommand {
  appointmentId: string;
  cancellationReason: AppointmentCancellationReason;
  /** Optional free-text note, max 500 chars. `Cancel` accepts it; `CancelRecurring` does not. */
  cancellationNote?: string;
  patientId?: string;
  practitionerId?: string;
  staffId?: string;
}

export interface UpdateAppointmentStatusCommand {
  appointmentId: string;
  updatedBy: string;
  status: AppointmentStatus;
}

export interface UpdateAppointmentCommand extends AppointmentResponse {
  residentialAddressId?: string;
  callBackContractId?: string;
  addedServices?: string[];
  editRecurring?: EditRecurring;
}

export interface EditRecurring {
  allAppointment: boolean;
  appointmentDate?: string;
  dataUpdate?: string[];
}

export interface RescheduleAppointmentCommand {
  appointmentId: string;
  startDate: string;
  endDate: string;
}

export interface AppointmentsByPatientIdRequest {
  startDate?: string;
  endDate?: string;
  practitioners?: string[];
  practices?: string[];
  /** Full status objects, not codes — Flutter ships `status.map((x) => x.toJson())`. */
  status?: Coding[];
  location?: string[];
  types?: Coding[];
  filter: DateFilter;
  pageNumber: number;
  pageSize: number;
  patientId: string;
  /** Flutter `toGetByPatientIdJson` always sends this; `false` on the patient tab. */
  isTimeSelected?: boolean;
}

export interface FuRequestFilter {
  organizationId: string;
  practiceId?: string;
  searchText?: string;
  pageNumber: number;
  pageSize: number;
  isActive: boolean;
}

export interface CancelFollowUpRequest {
  fuRequestId: string;
  cancellationReason: AppointmentCancellationReason;
}

/**
 * POST `/api/Appointment/SendBulkReminder` body. Even a single-row
 * "Send Reminder" row action ships a 1-element array (Flutter
 * `fu_requests_tab.dart:498-503`). Mirrors Flutter
 * `SendBulkReminderRequest`
 * (`scheduling_module/data/datamodels/send_reminder_request.dart:13-32`).
 */
export interface SendBulkReminderRequest {
  fuRequestIds: string[];
}

export interface CancelRecurringRequest {
  recurringId: string;
  cancellationReason: AppointmentCancellationReason;
  patientId?: string;
  practitionerId?: string;
  staffId?: string;
  allAppointment: boolean;
  appointmentDate?: string;
}

export interface BlockSlotCommand {
  id?: string;
  actorId: string;
  practiceId: string;
  start: string;
  end: string;
  comment?: string;
  recurring?: BlockRecurrenceCriteria;
}

export interface BlockRecurrenceCriteria {
  id?: string;
  providerId: string;
  practiceId: string;
  appointmentProfileId: string;
  startDate: string;
  endDate: string;
  repeatEvery: number;
  numberOfRepeat: number;
  period: 'Days' | 'Weeks' | 'Months' | 'Years';
  days?: number[];
}

// === Response Wrappers ===

export interface AppointmentsDetailsResponse {
  count: number;
  currentPage: number;
  totalPage: number;
  waitingRoom: number;
  cancelled: number;
  noShow: number;
  /**
   * Flat list. Populated for `filter: 'Day'` and `filter: 'Week'`. For
   * `filter: 'Month'` the backend OMITS this key entirely (the field is
   * absent from the JSON, not just empty) and ships data in
   * `appointmentByGroupBy` instead — same pattern as Doctor / Room
   * views per Flutter `calendar_by_month_widget.dart:296-316` and
   * `calendar_by_doctor_widget.dart:110-130`.
   */
  appointments?: ListAppointmentResponse[];
  /**
   * Per-day groups returned when `filter: 'Month'`. Each group has a
   * `appointmentDate` (the day's anchor) and an `appointmentList` of
   * appointments scheduled that day. PROD-963 — React previously only
   * read `appointments` (the flat list), missing the entire Month view
   * dataset.
   */
  appointmentByGroupBy?: AppointmentResponseGroupByMonth[];
  blockAppointment?: BlockAppointmentRequest[];
  /**
   * Faceted filter data — present on `POST /api/Appointment/ByPatientId`
   * (the patient-tab list). Feeds the doctor/provider cards + the Practice /
   * Doctors drawer filters. Verified against the live response 2026-06-02.
   * Absent on the calendar `appointments` endpoints.
   */
  facets?: AppointmentFacets;
}

export interface AppointmentFacets {
  provider?: AppointmentFacetProvider[];
  /** Backend ships DUPLICATE practice rows (same id repeated) — dedupe by `id`. */
  practice?: AppointmentFacetPractice[];
  /**
   * PROD-2887 — the patient's devices, built server-side the way `provider`
   * is: every device on the patient's appointments in any practice, archived
   * ones included, named by PracticeManagement.
   */
  device?: AppointmentFacetDevice[];
}

/** PROD-2887 — one device in `facets.device` (`DeviceDetailResponse`). */
export interface AppointmentFacetDevice {
  id: string;
  name?: string | null;
  practiceId?: string;
  isArchived?: boolean;
  /** Same misspelling as the provider facet — the patient's upcoming sessions on this device. */
  upcommingAppointment?: number;
}

/**
 * Provider facet (Flutter `ProviderData`). NOTE the speciality envelopes use
 * the `SpecialityWire` shape (`code.displayName`), NOT the `{code,display}`
 * shape that `ProviderDetailResponse` declares — so this is a distinct type.
 * `upcommingAppointment` (sic) and `doctorSpecialitiesIds` (sic, always null)
 * are spelled verbatim from the wire.
 */
export interface AppointmentFacetProvider {
  id: string;
  name?: string;
  photo?: string;
  speciality?: SpecialityWire;
  timeZone?: string;
  organizationId?: string;
  userId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  /** Backend misspelling preserved — count of the provider's upcoming appointments. */
  upcommingAppointment?: number;
  doctorSpecialities?: SpecialityWire[];
  doctorSpecialityIds?: string[];
  doctorSpecialitiesIds?: string[] | null;
}

export interface AppointmentFacetPractice {
  id: string;
  name?: string;
  phone?: string;
  address?: string;
  country?: string;
  city?: string;
  office?: string;
  faxNumber?: string;
  photo?: string;
  latitude?: string;
  longitude?: string;
  nabidhAssigningAuthority?: string;
  licenseNumber?: string;
  licenseAuthority?: string;
}

export interface AppointmentResponseByMonth {
  count: number;
  currentPage: number;
  totalPage: number;
  waitingRoom: number;
  cancelled: number;
  noShow: number;
  appointmentByGroupBy: AppointmentResponseGroupByMonth[];
  blockAppointment: BlockAppointmentRequest[];
}

export interface AppointmentResponseGroupByMonth {
  appointmentDate: string;
  appointmentList: ListAppointmentResponseByMonth[];
  /**
   * The number the month cell prints as "+{count} more". Flutter uses this
   * RAW backend value verbatim — it renders every returned appointment AND
   * shows "+{count} more" with no subtraction (`calendar_by_month_widget.dart`
   * `getMoreAppointment:395` + `:185-220`). Mirror that exactly; do not derive
   * it from `appointmentList.length`.
   */
  count: number;
}

/**
 * Block-availability entry as it appears in the Month response's
 * `blockAppointment[]` (verified against the dev `filter:'Month'` curl).
 * NOTE: distinct from `BlockAppointmentRequest` (the create shape) — the
 * reason is `note` (not `comment`) and it carries a `recurringId`. Flutter
 * `calendar_by_month_widget.dart:317-336` merges these into each day by
 * `blockDate` and renders them as block cards.
 */
export interface MonthBlockAppointment {
  id: string;
  note?: string | null;
  blockDate?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  recurringId?: string | null;
}

export interface AppointmentResponseByRoom {
  count: number;
  currentPage: number;
  totalPage: number;
  waitingRoom: number;
  cancelled: number;
  noShow: number;
  appointmentByGroupBy: AppointmentResponseGroupBy[];
  blockAppointment: BlockAppointmentRequest[];
}

export interface AppointmentResponseGroupBy {
  groupName: string;
  appointmentList: ListAppointmentResponseByMonth[];
}

export interface BlockAppointmentRequest {
  id: string;
  blockDate: string;
  startDate: string;
  endDate: string;
  startTime: TimeModel;
  endTime: TimeModel;
  comment?: string;
  practitionerId: string;
  recurring?: RecurringInfo;
}

export interface PatientAppointmentsResponse {
  currentPage: number;
  totalPage: number;
  count: number;
  appointments: AppointmentResponse[];
}

export interface RecurringAppointmentsResponse {
  count: number;
  currentPage: number;
  totalPage: number;
  appointments: ListAppointmentResponse[];
}

/**
 * Per-row shape of `POST /api/Appointment/Request` response. Mirrors
 * Flutter `FuRequest`
 * (`scheduling_module/data/datamodels/fu_request_list_response.dart:52-91`)
 * plus a backend extension (`specialty`) that the Flutter model
 * doesn't carry but the live wire ships per row.
 *
 * Wire shape verified against 2026-05-20 dev curl (active-requests
 * view, `count: 1365`, populated `fuRequests[]`).
 *
 * Wire-shape notes:
 *   - `reasonCode.code` ships as UPPERCASE GUID — backend doesn't
 *     normalize to lowercase. Don't normalize client-side either.
 *   - `followUpUnit` is a display string ("1 Days", "2 Days",
 *     "1 Weeks", "1 Months") — pluralised even for `1`. Treat as a
 *     pre-formatted label.
 *   - `lastReminder`, `canceledOn`, `cancelationReason`, `location`,
 *     `serviceCategory`, `serviceType`, `specialty` all observed
 *     `null` in the active-requests view — typed as
 *     `Foo | null`.
 *   - `specialty` was REMOVED in sweep PR #251 as "phantom" (the
 *     Flutter model doesn't ship it). The 2026-05-20 dev curl proved
 *     the backend ships it per row, always `null` in observed
 *     responses. Restored here.
 */
export interface FuRequestResponse {
  id: string;
  appointmentId: string;
  practiceId: string;
  requestedOn: string;
  reasonCode: ReasonCode;
  patient: PatientResponse;
  provider: ProviderResponse;
  contactNumber?: string | null;
  followUpUnit?: string | null;
  followUpIn: string;
  lastReminder?: string | null;
  status: 'Requested' | 'Completed' | 'Cancelled' | string;
  canceledOn?: string | null;
  cancelationReason?: AppointmentCancellationReason | null;
  createdOn?: string | null;
  location?: LocationModel | null;
  serviceCategory?: ServiceCategory | null;
  serviceType?: ServiceType | null;
  /**
   * Backend ships this field per row — observed `null` in the
   * 2026-05-20 dev curl for `POST /api/Appointment/Request` but the
   * field IS on the wire.
   *
   * **DO NOT REMOVE.** This field was speculatively removed in sweep
   * PR #251 (chore: scheduling type-drift sweep) on the basis that
   * Flutter's `FuRequest` model doesn't declare it. The 2026-05-20
   * curl proved that was wrong — the backend ships `specialty` even
   * though Flutter ignores it. Restored in PR #266 / PROD-847 with
   * full field-level doc citing both the sweep regression and the
   * curl date so a future reviewer can't re-remove it without
   * checking the wire again.
   */
  specialty?: Speciality | null;
}

export interface FuRequestListResponse {
  count: number;
  currentPage: number;
  totalPage: number;
  fuRequests: FuRequestResponse[];
}

export interface FlagListResponse {
  categoryName: string;
  flagResponse: FlagResponse[];
}

export type SearchValueSet =
  | 'AppointmentCancellationReason'
  | 'AppointmentType'
  | 'AppointmentStatus'
  | 'ReasonsForBookAppointment';

export interface ValueSetResponse {
  code: string;
  definition: string;
  display: string;
}

/**
 * Backend-provided provider credential/license flag, shown as a badge on
 * the calendar provider strip. Mirrors Flutter `Issue` on
 * `DoctorListResponse` (`doctor_list_response.dart:249`): `type` is
 * `"error"` (red) or `"warning"` (amber), `message` is the tooltip text
 * (e.g. expired/invalid license). Present only when the provider has an
 * issue — absent otherwise.
 */
export interface ProviderIssue {
  type?: string;
  message?: string;
}

export interface ProviderDetailResponse {
  id: string;
  name: string;
  photo?: string;
  speciality: Speciality;
  /** Credential/license issue flag — see {@link ProviderIssue}. */
  issue?: ProviderIssue | null;
  // Some wire callers (provider-section, provider-selection-view) read
  // a discrete `specialityId` companion field — present on the wire,
  // not consistently surfaced in the typed shape. Optional.
  specialityId?: string;
  timeZone?: string;
  organizationId: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  upcommingAppointment: number;
  doctorSpecialities?: Speciality[];
  doctorSpecialityIds?: string[];
}

export interface SchedulingActionObject {
  status: 'Success' | 'Failure';
  data?: unknown;
  blockers?: unknown[];
}

// === PreLoadAppointmentDetails (POST /api/v1/Practice/PreLoadAppointmentDetails) ===
// Powers the appointment-popup-card opened by clicking any calendar appointment.
// Flutter source: scheduling_data_source_impl.dart:945-977 (request) +
// pre_load_appointment_model.dart (response shape).
//
// Wire-shape contract:
//   - `billing` and `patient` are flag-as-object: `{}` = load, `null` = skip
//   - For the popup we always ship both `{}` because the panel needs invoice
//     + patient data to render the Payment side and the patient fields.
//   - All 6 fields are required (per Flutter line 953-960). Use `null` for
//     intentional sentinels, never omit the key.

export interface PreLoadAppointmentRequest {
  appointmentId: string;
  patientId: string;
  providerId: string;
  /** `{}` = load invoice, `null` = skip. Popup ships `{}`. */
  billing: Record<string, never> | null;
  /** `{}` = load patient, `null` = skip. Popup ships `{}`. */
  patient: Record<string, never> | null;
  appointmentProfileId: string;
}

export interface PreLoadedPatientData {
  patient?: PatientResponse;
  /** Master-data lists — popup ignores these; included for the edit flow. */
  genders?: Array<{ id: string; name: string }>;
  identityTypes?: Array<{ id: string; name: string }>;
  maritalStatuses?: Array<{ id: string; name: string }>;
  ownerTypes?: Array<{ id: string; name: string }>;
}

export interface BillingProfilesData {
  /** Backend ships nested profile arrays; popup only needs invoice totals. */
  billingProfiles?: unknown[];
  defaultBillingProfile?: unknown;
}

export interface PreLoadBillingData {
  invoice?: AppointmentInvoiceModel;
  /**
   * Patient financial summary shown as the two stat cards atop the billing
   * tab. Wire shape (PreLoadAppointmentDetails, verified 2026-06-04):
   *   { patientId, balance: [{ subject, amount, financialEventStatus }] }
   * `subject` is "Total Unpaid" / "Available Balance"; `financialEventStatus`
   * is "negative" (owed) / "positive" (credit). Flutter
   * `PatientAvailableBalanceWidget`.
   */
  availableBalance?: {
    patientId?: string;
    balance?: Array<{
      subject?: string;
      amount?: number;
      financialEventStatus?: string;
    }>;
  };
  receipts?: unknown[];
}

export interface PreLoadAppointmentResponse {
  appointment?: AppointmentResponse;
  billingProfiles?: BillingProfilesData;
  providerReasonForVisits?: AppointmentValueSets[];
  patientEmergencyContacts?: unknown[];
  billingData?: PreLoadBillingData;
  patientData?: PreLoadedPatientData;
}

// ── Questionnaire response shapes (PROD-843) ─────────────────────────────
//
// Wire shape verified against the 2026-05-20 dev curl for
// `GET /api/QuestionnaireResponses/ForPatient?appointmentId=<id>`. Mirrors
// Flutter `QuestionnaireSubmitted` in
// `scheduling_module/data/datamodels/questionnaire_submitted_model.dart`,
// with two backend-added fields the Flutter model doesn't carry:
//   - `questionnaireName`  → backend convenience for display
//   - `updatedByName`      → backend convenience (ships as "  " when
//                            never updated)
//
// Wire-shape quirks (do NOT normalize):
//   - URL query param `appointmentId` is **lowercase** (verbatim)
//   - Top-level array is `item` SINGULAR (nested Group items use `items`
//     PLURAL — see QuestionnaireSubmittedItem.items)
//   - `subject.patientid` and `questionnaire.questionnaireid` are
//     all-lowercase
//   - `_comment` leading-underscore on subject/source/author is FHIR
//     extension convention — ship verbatim

export interface QuestionnaireValueCoding {
  code?: string | null;
  display?: string | null;
  system?: string | null;
}

export interface QuestionnaireAnswer {
  /** Boolean answer for `type: 'Boolean'` items. Flutter ALWAYS ships all
   *  3 answer fields on save even when unused — see save curl 2026-05-20. */
  valueBoolean?: boolean | null;
  /** Free-text answer for `type: 'String'` items. */
  valueString?: string | null;
  /** Selected option(s) for `type: 'Choice'` and `type: 'OpenChoice'`. */
  valueCoding?: QuestionnaireValueCoding[] | null;
}

/** 5 question types, copied verbatim from Flutter constants. */
export type QuestionnaireItemType =
  | 'String'
  | 'Boolean'
  | 'Choice'
  | 'OpenChoice'
  | 'Group';

export interface QuestionnaireSubmittedItem {
  questionId?: string | null;
  /** Hierarchical-path identifier — Flutter wire form e.g.
   *  `"Marina Dubai New Office/David Schwartz/Sampe form/Intoxications/Q1"`. */
  linkId?: string | null;
  text?: string | null;
  type?: QuestionnaireItemType | string;
  answer?: QuestionnaireAnswer | null;
  /** Per-item free-text annotation. Dart types as `dynamic` — we narrow
   *  to `string | null` since every observed value is a string. */
  comment?: string | null;
  id?: string | null;
  submittedOn?: string | null;
  /** Nested items — plural — for `type: 'Group'` parents only. */
  items?: QuestionnaireSubmittedItem[] | null;
}

export interface QuestionnaireSubjectRef {
  /** FHIR extension convention — Flutter ships leading underscore. */
  _comment?: string | null;
  /** All-lowercase on the wire — do NOT normalize to `patientId`. */
  patientid?: string | null;
}

export interface QuestionnaireSourceRef {
  _comment?: string | null;
  sourceId?: string | null;
}

export interface QuestionnaireAuthorRef {
  _comment?: string | null;
  practitionerId?: string | null;
}

export interface QuestionnaireDataRef {
  /** All-lowercase on the wire — do NOT normalize to `questionnaireId`. */
  questionnaireid?: string | null;
}

export interface QuestionnaireSubmitted {
  id?: string | null;
  comment?: string | null;
  identifier?: string | null;
  questionnaire?: QuestionnaireDataRef | null;
  /** Observed values: `"InProgress"`, `"Completed"`, `"Active"`. */
  status?: string | null;
  subject?: QuestionnaireSubjectRef | null;
  updatedBy?: string | null;
  createdBy?: string | null;
  author?: QuestionnaireAuthorRef | null;
  /** ISO-8601 with timezone offset (e.g. `"2026-05-20T14:18:56.6160123+00:00"`). */
  authored?: string | null;
  source?: QuestionnaireSourceRef | null;
  /** **Singular at the top level** — Flutter parity. Nested Group items
   *  use plural `items`. */
  item?: QuestionnaireSubmittedItem[] | null;

  /** Backend-added (not in Flutter `QuestionnaireSubmitted`). Display label
   *  pulled from the parent Questionnaire — saves a lookup. */
  questionnaireName?: string | null;
  /** Backend-added. Empty string `"  "` when never updated. */
  updatedByName?: string | null;
}

// ── Questionnaire DEFINITION shapes (PROD-845 PR-2) ──────────────────────
//
// Wire shape verified against the 2026-05-20 dev curl for
// `POST /api/Questionnaires/PatientQuestionnaire`. Mirrors Flutter
// `Questionnaire` in `scheduling_module/data/datamodels/questionnaire_model.dart`
// with backend additions that the Flutter model doesn't carry but the
// live wire ships (`groups`, `questionType`, `answerOptions`,
// `required`, `maxLength`, `order`, `isUpdated`).
//
// Definition vs. Response distinction:
//   - GET `/QuestionnaireResponses/ForPatient` (PR-1) → `QuestionnaireSubmitted`
//     (the patient's answers, read-only render)
//   - POST `/Questionnaires/PatientQuestionnaire` (PR-2) → `QuestionnaireDefinition`
//     (the blank form template with question text + answer options for
//     the editable render)
//   - POST `/QuestionnaireResponses` (PR-2) → `QuestionnaireResponseSavePayload`
//     (the save request body — different shape from `QuestionnaireSubmitted`!)

/** Body for `POST /api/Questionnaires/PatientQuestionnaire`. Flutter
 *  hand-builds the JSON string (`scheduling_data_source_impl.dart:392`)
 *  so all three fields ship even when null. */
export interface QuestionnaireDefinitionRequest {
  practitionerId: string | null;
  practiceId: string | null;
  formId: string | null;
}

/** Per-item question-type envelope. Backend-added — not in Flutter
 *  `Item.type` (which is just a string). */
export interface QuestionnaireQuestionType {
  type: QuestionnaireItemType | string;
  title?: string | null;
  isActive?: boolean | null;
}

/** Category / group metadata attached to each item. Backend-added. */
export interface QuestionnaireItemGroup {
  id?: string | null;
  name?: string | null;
  url?: string | null;
  icon?: string | null;
  isSearchable?: boolean | null;
}

/** Pre-defined answer choices for `Choice` / `OpenChoice` items. */
export interface QuestionnaireAnswerOption {
  valueCoding?: QuestionnaireValueCoding | null;
}

export interface QuestionnaireDefinitionItem {
  id?: string | null;
  /** Hierarchical-path identifier (e.g. `"Marina/David/Sampe form/Q1"`). */
  linkId?: string | null;
  type?: QuestionnaireItemType | string;
  practitionerId?: string | null;
  specialtyId?: string | null;
  groupId?: string | null;
  text?: string | null;
  /** Mandatory-field flag. Plan A (PROD-845): the React save-enable
   *  check recurses into Group children to enforce required leaves
   *  properly — diverges from Flutter's buggy flat-loop at
   *  `appointment_form_widget.dart:127-130`. Backend (verified
   *  2026-05-20 in `QuestionnaireResponseService.cs`) doesn't validate
   *  completeness server-side, so this is a pure client-side UX gate. */
  required?: boolean | null;
  maxLength?: number | null;
  order?: number | null;
  date?: string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  isArchive?: boolean | null;
  isUpdated?: boolean | null;
  questionType?: QuestionnaireQuestionType | null;
  groups?: QuestionnaireItemGroup | null;
  /** Nested questions for `type: 'Group'` parents. */
  items?: QuestionnaireDefinitionItem[] | null;
  /** Pre-defined choices for `Choice` / `OpenChoice`. */
  answerOptions?: QuestionnaireAnswerOption[] | null;
}

export interface QuestionnaireDefinition {
  id?: string | null;
  items?: QuestionnaireDefinitionItem[] | null;
  date?: string | null;
  publisher?: string | null;
  isArchive?: boolean | null;
  identifier?: string | null;
  subjectType?: string | null;
  title?: string | null;
  description?: string | null;
  /** Observed: `"Active"`, `"Retired"`. */
  status?: string | null;
  organizationId?: string | null;
  practitionerId?: string | null;
  questionnaireRuleId?: string | null;
  specialtyId?: string | null;
  version?: string | null;
  updatedOn?: string | null;
  isUpdated?: boolean | null;
  isDefault?: boolean | null;
  practiceId?: string | null;
  questionnaireRule?: { id?: string | null; title?: string | null } | null;
  speciality?: {
    specialitySkillId?: string | null;
    code?: { code?: string | null; displayName?: string | null; system?: string | null } | null;
  } | null;
}

// ── Questionnaire SAVE-REQUEST shape (PROD-845 PR-2) ─────────────────────
//
// Wire shape verified against the 2026-05-20 dev curl for
// `POST /api/QuestionnaireResponses`. **NOT THE SAME SHAPE** as
// `QuestionnaireSubmitted` (the GET-response shape): the save body
// ships `subject` / `author` as PLAIN GUID STRINGS, not as the
// `{ _comment, patientid }` / `{ _comment, practitionerId }` objects
// that the GET response carries. Wire-shape asymmetry — keep both
// types distinct, don't try to reuse.
//
// Every item ships all 3 `answer.value*` fields even when unused
// (Flutter does NOT prune; `appointment_form_widget.dart` builds the
// full Answer object for every item type).

/** Per-item answer payload — Flutter ALWAYS ships all 3 fields. */
export interface QuestionnaireResponseAnswerPayload {
  valueBoolean: boolean;
  valueString: string;
  valueCoding: QuestionnaireValueCoding[];
}

export interface QuestionnaireResponseItemPayload {
  questionId: string;
  linkId: string;
  text: string;
  type: QuestionnaireItemType | string;
  answer: QuestionnaireResponseAnswerPayload;
  /** Nested items for `type: 'Group'` parents. Empty array `[]` for
   *  non-Group items (Flutter sends `[]`, not `null`). */
  items: QuestionnaireResponseItemPayload[];
}

export interface QuestionnaireResponseSavePayload {
  questionnaireId: string;
  /** ⚠ PLAIN GUID string — NOT `{ _comment, patientid }`. The GET
   *  response uses the object shape; the save request does not. */
  subject: string;
  /** ⚠ PLAIN GUID string — NOT `{ _comment, practitionerId }`. */
  author: string;
  /** Defaults to null in observed dev curls (no guardian/proxy
   *  scenarios captured yet). */
  sourceId: string | null;
  appointmentId: string;
  /** Plural at the top level — vs `QuestionnaireSubmitted.item`
   *  singular for the GET response. */
  items: QuestionnaireResponseItemPayload[];
}

// ───────────────────────────────────────────────────────────────────────────
// Lab linkage (Phlebotomist-only order_requests_tab) — un-deferred 2026-05-21.
// Wire shapes derived from Flutter `scheduling_module/data/datamodels/` +
// `domian/usecases/`. Verified against `scheduling_data_source_impl.dart:
// 1158-1212`. Dev curls not captured per auto-mode authorization — fixes
// will land in a follow-up if any wire-shape drift surfaces at QA.
// ───────────────────────────────────────────────────────────────────────────

/**
 * Lab order status values — Flutter verbatim
 * (`scrips_core/lib/constants/app_constants.dart:809-820`).
 * Statuses are stored on `PatientLabOrderDetail.orderStatus` AND sent on
 * the `UpdatePatientLabRequest` PUT body. Strings must match the backend
 * literals exactly — any case shift breaks the filter.
 */
export const LAB_ORDER_STATUS = {
  Requested: 'Requested',
  SampleCollected: 'Sample collected',
  ResultReceived: 'Result received',
  ResultReviewed: 'Result reviewed',
  /**
   * PROD-2660: set by Patient (PROD-2629, `LabRequestStatus.Cancelled`) when a
   * still-Requested lab is removed from a signed plan. Terminal for the lab
   * desk: no collect, no result link. Never counts as pending.
   */
  Cancelled: 'Cancelled',
} as const;

export type LabOrderStatus =
  (typeof LAB_ORDER_STATUS)[keyof typeof LAB_ORDER_STATUS];

/**
 * Lab order request — `POST /api/Patients/GetPatientLabDetails` body.
 * Mirrors Flutter `OrderRequestsListRequest`.
 */
export interface OrderRequestsListRequest {
  organizationId?: string | null;
  providerId?: string | null;
  patientId?: string | null;
  search?: string | null;
  pageNumber?: number | null;
  pageSize?: number | null;
  status?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  /**
   * PROD-2653: only requests sent to this lab vendor (Scrips.Patient #994).
   * A body field, not a query string. Omit (or null) for all vendors.
   */
  labVendorId?: string | null;
}

/**
 * One row in the lab orders list. Mirrors Flutter `PatientLabOrderDetail`
 * + 3 additional fields the live response includes that Flutter's
 * `fromJson` doesn't read (`notePlainText`, `patientDocumentId`,
 * `labDetails`). Verified against the 2026-05-21 dev curl (Andrew —
 * PR #295 §1 fixup); every field nullable because the backend ships
 * `null` for unset values across the board.
 */
export interface PatientLabOrderDetail {
  id?: string | null;
  encounterId?: string | null;
  encounterNumber?: string | null;
  patientId?: string | null;
  patientName?: string | null;
  mrn?: string | null;
  contactNumber?: string | null;
  orderStatus?: string | null;
  orderDescription?: string | null;
  /**
   * Free-text clinical note attached to the order. Ships as `null`
   * for most rows. Flutter's `fromJson` doesn't read this but it's
   * present on every wire response — added 2026-05-21 from dev curl.
   */
  notePlainText?: string | null;
  orderDate?: string | null;
  orderName?: string | null;
  cptCode?: string | null;
  orderReqBy?: string | null;
  orderReqById?: string | null;
  /**
   * The linked patient-document GUID once `ReceivedLabResults` has
   * been fired against this row (matching `Result received` status).
   * Pre-link rows ship `null`. Added 2026-05-21 from dev curl.
   */
  patientDocumentId?: string | null;
  documentUrl?: string | null;
  /**
   * JSON-encoded STRING (not an array) of the test list — backend
   * stores it as a serialised JSON column. Ships as `"[]"` for
   * rows with no tests, otherwise e.g.
   * `'[{"Code":"10500","Display":"...","System":"IHL"}]'`.
   * Consumers should prefer the parallel `labOrderValueSetTestDetails`
   * field below (already deserialised); `labDetails` is the raw
   * column value, left typed as `string` to mirror backend storage.
   * Added 2026-05-21 from dev curl.
   */
  labDetails?: string | null;
  /** Master-code list of individual tests in this lab order. */
  labOrderValueSetTestDetails?: Coding[] | null;
  /**
   * PROD-2653: the lab vendor the request was sent to, stamped at signing
   * (Scrips.Patient #994). Both null on rows signed before the vendor DB
   * script ran; the desk renders those as "—".
   */
  labVendorId?: string | null;
  labVendorName?: string | null;
  /**
   * PROD-2846 (Scrips.Patient #1022, PROD-2841): `ContractedLab` |
   * `PatientChoice`; null on legacy rows and before AddLabRequisitions.sql.
   */
  fulfilmentMode?: LabFulfilmentMode | string | null;
  /** The requisition the request belongs to (one per lab per encounter); null when none. */
  requisitionId?: string | null;
  /** null = ordered in the A&P; `Reflex` | `Unsolicited` = performed by the lab, arrived with a result. */
  source?: LabRequestSource | string | null;
  /** For Reflex / Unsolicited: the ordered lab request whose result brought it. */
  parentLabRequestId?: string | null;
}

/** PROD-2846: how a lab order is fulfilled (Scrips.Patient PROD-2841). */
export const LAB_FULFILMENT_MODE = {
  ContractedLab: 'ContractedLab',
  PatientChoice: 'PatientChoice',
} as const;
export type LabFulfilmentMode =
  (typeof LAB_FULFILMENT_MODE)[keyof typeof LAB_FULFILMENT_MODE];

/** PROD-2846: where a lab request came from (Scrips.Patient PROD-2841). */
export const LAB_REQUEST_SOURCE = {
  Order: 'Order',
  Reflex: 'Reflex',
  Unsolicited: 'Unsolicited',
} as const;
export type LabRequestSource =
  (typeof LAB_REQUEST_SOURCE)[keyof typeof LAB_REQUEST_SOURCE];

/** One test in a requisition (`LabRequisitionTestDto`). */
export interface LabRequisitionTest {
  labRequestId: string;
  labRequestTestId?: string | null;
  code?: string | null;
  system?: string | null;
  display?: string | null;
  status?: string | null;
  orderCode?: string | null;
  orderSystem?: string | null;
  orderDisplay?: string | null;
  source?: LabRequestSource | string | null;
  parentLabRequestId?: string | null;
  labPriceLineId?: string | null;
}

/** One requisition: one per contracted lab, plus at most one Patient's choice. */
export interface LabRequisition {
  id: string;
  /** null = Patient's choice of lab. */
  labVendorId?: string | null;
  labVendorName?: string | null;
  fulfilmentMode?: LabFulfilmentMode | string | null;
  /** `Requested` | `Sent` | `NotSent` (Patient's choice) | `Cancelled`. */
  status?: string | null;
  billedByClinic?: boolean | null;
  sentByClinic?: boolean | null;
  createdOn?: string | null;
  createdBy?: string | null;
  tests?: LabRequisitionTest[] | null;
}

/**
 * PROD-2846: `GET /api/Patients/{patientId}/Encounters/{encounterId}/LabRequisitions`
 * (Scrips.Patient #1022). `storeAvailable` is false until AddLabRequisitions.sql
 * has run; `requisitions` is then empty. Never carries costs.
 */
export interface EncounterLabRequisitions {
  patientId: string;
  encounterId: string;
  storeAvailable: boolean;
  testsAvailable?: boolean | null;
  requisitions?: LabRequisition[] | null;
}

/** Pageable response for `GetPatientLabDetails`. */
export interface GetPatientsLabDetailResponse {
  patientLabOrderDetails?: PatientLabOrderDetail[] | null;
  pageNumber?: number | null;
  pageSize?: number | null;
  totalNumberOfPages?: number | null;
  totalNumberOfRecords?: number | null;
}

/**
 * `PUT /api/Patients/UpdatePatientLabRequest?Id={id}` body.
 * Mirrors Flutter `UpdatePatientLabRequestStatusParams` — `Id` is URL
 * query-string, `status` is the body field (literal LAB_ORDER_STATUS value).
 */
export interface UpdatePatientLabRequestBody {
  status: string;
}

/**
 * `POST /api/Patients/ReceivedLabResults` body. Mirrors Flutter
 * `LinkLabResultParams`. `labRequestIds` is a multi-row link (the same
 * uploaded document can be linked to multiple lab orders).
 */
export interface LinkLabResultRequest {
  labRequestIds: string[];
  patientDocumentId: string;
}

// ── Devices on the calendar (PROD-2007) ─────────────────────────────────────
//
// A resource IS a device (ruling 2026-09-11); it has its own schedule, its
// own products (appointment profiles) and its own bookings. Nothing links a
// device booking to a practitioner.

/**
 * One device of a practice as `GET /api/Resource?practiceId=&status=active`
 * returns it (`ResourceResponse`, PracticeManagement PR #944). Mirrors the
 * settings `ResourceRecord`; scheduling carries its own copy because domains
 * never cross-import (`src/settings` ↔ `src/scheduling`).
 */
export interface CalendarDevice {
  id: string;
  name: string;
  type: string;
  practiceId: string;
  status: 'active' | 'archived';
  subtype?: string | null;
  identifier?: string | null;
  /** `dbo.PracticeExamRooms.Id`; null = mobile / no room. */
  roomId?: string | null;
  roomName?: string | null;
  technicianId?: string | null;
  technicianName?: string | null;
}

/** Page envelope of `GET /api/Resource` (`ResourceListResponse`). */
export interface CalendarDeviceListResponse {
  resources: CalendarDevice[];
  totalCount: number;
  activeCount: number;
  archivedCount: number;
  filteredCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * A device product — `GET /api/Resource/{resourceId}/Schedule/AppointmentProfiles`
 * (`ResourceAppointmentProfileResponse`, PracticeManagement PR #945). The
 * product decides a booking's length and colour.
 */
export interface DeviceAppointmentProfile {
  id: string;
  resourceId: string;
  profileName: string;
  minutesDuration: number;
  /** Display colour, e.g. `#0EA5E9`; null when the product has none. */
  color?: string | null;
  /** `dbo.AppointmentTypes.Code`; optional. */
  appointmentTypeId?: string | null;
  /**
   * The product's own default billing profile (PM PR #946 / PROD-2154). When
   * set, a device booking is invoiceable WITHOUT added services: Scheduling's
   * `ResolveBillingProfileId` falls back to it.
   *
   * Read-only to the booking flow. It is deliberately NOT echoed back on the
   * request: the server treats a sent value as PRECEDENCE, not a merge
   * (requested wins, else the product's), so replaying a snapshot from a
   * cached product list would override a billing profile the practice has
   * since changed.
   */
  billingProfileId?: string | null;
  /**
   * The product's own priced service (PM PR #947 / PROD-2165) — "HBOT 60" IS
   * LW-017 at 750. When set, a device booking is invoiceable WITHOUT added
   * services, exactly as `billingProfileId` makes it: Scheduling refuses only
   * when there is no service, no billing profile AND no added services.
   *
   * Read-only to the booking flow and NOT echoed back on the request, for the
   * same reason as `billingProfileId`: the server re-reads the product itself,
   * and replaying a cached snapshot would pin a price the practice may have
   * changed since the list was fetched.
   */
  serviceId?: string | null;
  isDefault: boolean;
  inAppBooking: boolean;
  isArchived: boolean;
  lastUpdated?: string;
}

/**
 * One bookable window from `GET /api/Slots/ResourceAvailability` — a
 * `TimePeriodLibrary` `TimeRange` serialised, already moved into the caller's
 * time zone by the controller. Only `start` / `end` are read.
 */
export interface ResourceAvailabilityPeriod {
  start: string;
  end: string;
  duration?: string;
  isMoment?: boolean;
  isAnytime?: boolean;
  hasStart?: boolean;
  hasEnd?: boolean;
  isReadOnly?: boolean;
}

/** PROD-2383 — `CreateResourceAppointmentRequest.Recurring` (`ResourceRecurringRequest`). */
export interface DeviceRecurringRequest {
  startDate: string;
  endDate: string;
  repeatEvery: number;
  numberOfRepeat: number;
  period: string;
  /** Only for "Weeks"; `null` otherwise, as the provider body does. */
  days: string[] | null;
  /** Local wall clock, no `Z`. */
  slots: Array<{ start: string }>;
}

/**
 * `POST /api/Appointment/Book` body for a DEVICE booking (Scheduling PR #1964,
 * S12). The practitioner payload minus the practitioner: never
 * `practitionerId`, `resourceIds`, `end`, `recurring` or `reminderProfile`.
 * `end` is derived server-side from the product's minutes. The booking MUST
 * carry `appointmentProfile.billingProfileId` or non-empty `addedServices`
 * (service ids) or the server refuses with `DeviceBookingNeedsBillingProfile`.
 */
export interface DeviceBookingRequest {
  deviceId: string;
  deviceProfileId: string;
  /**
   * PROD-2383 — a recurring COURSE, on `POST /api/Appointment/BookResource`
   * only. Each session is its own appointment with its own invoice, all
   * sharing one RecurringId. A session is a start time and nothing else: the
   * product owns every window. Absent books one session, as before.
   */
  recurring?: DeviceRecurringRequest | null;
  patientId: string;
  practiceId: string;
  organizationId: string;
  /** Local wall clock, no `Z`: `YYYY-MM-DDTHH:mm:ss.sss`. */
  start: string;
  appointmentType: AppointmentType;
  specialty: SpecialityWire;
  location: LocationModel;
  reasonCode: ReasonCode;
  roomId?: string;
  priority: number;
  appointmentProfile: { billingProfileId?: string };
  addedServices: string[];
  addedIndividualServices: unknown[];
  sponsorType: string;
  sponsor: null;
  flagList: FlagResponse[];
  isCreatedByPatient: boolean;
  isRelatedPerson: boolean;
  createdBy: string;
  comment?: string | null;
  description?: string | null;
  invoice: InvoiceRequest;
}

/**
 * PROD-2526 — the body of `PUT /api/Appointment/Resource/{id}`, the DEVICE edit
 * route (Scheduling `UpdateResourceAppointmentRequest`, PROD-2385 / PROD-2383).
 *
 * Deliberately NOT the provider edit's echo shape: there is no participant
 * array to dig a provider out of, no reminder profile and no specialty. The
 * device and the patient are plain ids the server checks against the stored
 * booking (`DeviceImmutable` / `PatientImmutable`), and the practitioner-only
 * fields are pinned server-side. Built by `buildDeviceUpdateRequest`.
 */
export interface DeviceUpdateRequest {
  id: string;
  deviceId: string;
  patientId: string;
  /**
   * The profile SNAPSHOT id when the product is unchanged (the server updates
   * that row and its ReferenceId survives); the PRODUCT id when it changed (the
   * server misses it and creates a fresh snapshot whose ReferenceId is it).
   */
  appointmentProfileId: string;
  invoiceId: string;
  profileName: string;
  color: string | null;
  billingProfileId: string | null;
  location: LocationModel;
  organizationId: string;
  practiceId: string;
  roomId: string;
  appointmentType: AppointmentType;
  reasonCode: ReasonCode;
  status: AppointmentStatus;
  priority: number;
  /** Local wall clock, no `Z`. */
  start: string;
  end: string;
  minutesDuration: number;
  description: string | null;
  comment: string | null;
  patientInstruction: string | null;
  flagList: FlagResponse[];
  updatedBy: string;
  sponsorType: string | null;
  sponsor: Sponsor | null;
  policyNo: string | null;
  billingTotal: BillingTotal | null;
  paymentLink: string | null;
  residentialAddressId: string | null;
  callBackContractId: string | null;
  addedServices: string[];
  /** The course a session belongs to — set only for a series edit (this-and-following / all). */
  recurring: RecurringInfo | null;
  /** Which sessions a series edit reaches — `buildEditRecurringPayload`. Null edits this session alone. */
  editRecurring: { appointmentDate: string | null; allAppointment: boolean; dataUpdate: string[] } | null;
}
